import { Api, makeUrlParams } from "@/core/services/api.service";
import { OrganizationAdapter } from "@/core/models/organization";
import { QueryUrlBuilderService } from "@/core/services/queryUrlBuilder.service";
import useAccountStore from "@/apps/shared/organization/store/accountStore";

/**
 * @returns {Promise<Array>}
 */
const fetchOrganizationsList = async () => {
  const { id: accountId } = useAccountStore();

  const params = [`accountId=${accountId}`, "pagination=false"];

  const response = await Api.new().get(makeUrlParams(params, "organizations"), {
    headers: { accept: "application/json" },
  });

  return response.data;
};

/**
 * @returns {Promise<Array>}
 */
const fetchOrganizationsListForDashboard = async () => {
  const params = ["exists[parentOrganizationId]=false", "pagination=false"];

  const response = await Api.new().get(makeUrlParams(params, "organizations"), {
    headers: { accept: "application/json" },
  });

  return response.data;
};

/**
 * @param {object} params
 * @returns {Promise<{totalItems, items: *}>}
 */
const fetchOrganizationsListWithParams = async (params) => {
  const urlBuilder = new QueryUrlBuilderService("organizations");

  params.filters["exists[parentOrganizationId]"] = true;

  urlBuilder
    .withPagination(params.pagination.page, params.pagination.perPage, true)
    .withSort(params.sorting.sort, params.sorting.order)
    .withFilters(params.filters);

  const response = await Api.new().get(urlBuilder.get("organizations"), {
    headers: { accept: "application/ld+json" },
  });

  const data = response.data["hydra:member"];
  const organizations = data.map((organization) => OrganizationAdapter.adapt(organization));

  return {
    items: organizations,
    totalItems: response.data["hydra:totalItems"],
  };
};

const fetchOrganizationById = async (organizationId) => {
  const response = await Api.new().get(`organizations/${organizationId}`, {
    headers: { accept: "application/json" },
  });

  return OrganizationAdapter.adapt(response.data);
};

const fetchPublicDetailsOrganizationById = async (organizationId) => {
  const response = await Api.new().get(`organizations/${organizationId}/public`, {
    headers: { accept: "application/json" },
  });

  return OrganizationAdapter.adapt(response.data);
};

const fetchOrganizationsTree = async () => {
  const response = await Api.new().get("organizations/tree");

  return response.data;
};

const fetchActiveStores = async () => {
  const response = await Api.new().get("organizations?type=store&status=active&pagination=false", {
    headers: { accept: "application/json" },
  });
  return response.data;
};

const fetchManagementUnits = async () => {
  const response = await Api.new().get("organizations/available-parents", {
    headers: { accept: "application/json" },
  });

  return response.data;
};

const fetchOrganizationsRoot = async () => {
  const response = await Api.new().get("/organizations/root", {
    headers: { accept: "application/json" },
  });

  return response.data;
};

const createOrganization = async (newOrganizationData) => {
  const response = await Api.new().post("organizations", newOrganizationData);
  return OrganizationAdapter.adapt(response.data);
};

const updateOrganization = async (organizationId, updatedOrganizationData) => {
  const response = await Api.new().patch(`organizations/${organizationId}`, updatedOrganizationData, {
    headers: { "Content-Type": "application/merge-patch+json" },
  });
  return OrganizationAdapter.adapt(response.data);
};

const getCurrentOrganization = async (userId) => {
  const { id, commonName, moduleType, account } = (
    await Api.new().get(`/users/${userId}/current-organization`, {
      headers: { accept: "application/json" },
    })
  ).data;

  return {
    id,
    commonName,
    moduleType,
    account,
  };
};

/**
 * @param {string} organizationRootId
 */
const fetchOrganizationsCountPerModule = async (organizationRootId) => {
  return (await Api.new().get(`organizations/${organizationRootId}/module-count`)).data;
};

/**
 * @param {string} organizationRootId
 */
const fetchUsersCountByModule = async (organizationRootId) => {
  return (await Api.new().get(`organizations/${organizationRootId}/users-module-count`)).data;
};

/**
 * @param {string} id
 */
const isPaymentByBuyerFeatureAvailable = async (id) => {
  return (await Api.new().get(`payment/account/${id}/by-buyer-feature-available`)).data;
};

const deleteAccount = async (id) => {
  try {
    await Api.new().delete(`accounts/${id}`);
  } catch (e) {
    if (e.response.status === 404) {
      throw "account_deletion_error.account_not_found";
    }

    if (e.response.status === 422) {
      throw e.response.data.detail;
    }

    throw "account_deletion_error.unknown";
  }
};

const fetchWarehouses = async () => {
  const linkedWarehousesResponse = await Api.new().get(`organizations/connected-warehouses`, {
    headers: { accept: "application/json" },
  });

  if (linkedWarehousesResponse.data.length > 0) {
    return linkedWarehousesResponse.data;
  }

  const internalWarehousesResponse = await Api.new().get(`organizations?type=warehouse`, {
    headers: { accept: "application/json" },
  });

  return internalWarehousesResponse.data;
};

export default {
  createOrganization,
  fetchActiveStores,
  fetchManagementUnits,
  fetchOrganizationById,
  fetchOrganizationsCountPerModule,
  fetchOrganizationsList,
  fetchOrganizationsListForDashboard,
  fetchOrganizationsListWithParams,
  fetchOrganizationsRoot,
  fetchOrganizationsTree,
  fetchPublicDetailsOrganizationById,
  fetchUsersCountByModule,
  getCurrentOrganization,
  updateOrganization,
  isPaymentByBuyerFeatureAvailable,
  deleteAccount,
  fetchWarehouses,
};
